import type { LoadResult } from "./script-source.js";
import type { Point, Rect } from "./geometry.js";
import type {
  OverlayDefaults,
  OverlaySettingsState,
} from "./overlay-defaults.js";
import type {
  CaptureFormat,
  CaptureKind,
  DeviceState,
} from "./media-devices.js";
import type {
  CaptureCommand,
  CaptureEvent,
  RecordingStatus,
} from "./recording.js";
import type { RecoveryState } from "./recovery.js";
import type { NudgeDirection } from "./script-model.js";
import type { ScrollMode } from "./scroll-mode.js";
import type { StepDirection } from "./overlay-defaults.js";
import type { ShortcutCommand, ShortcutState } from "./shortcuts.js";
import type { TakeMarkerQuality } from "./sidecar.js";
import type { FollowerState } from "./speech.js";
import type { VoiceCommandState } from "./voice-commands.js";

/**
 * The IPC surface shared by main, preload and renderer. Kept type-only against
 * the core so it can be imported from the browser-side renderer without pulling
 * Node built-ins into the web bundle.
 */

/** Channel names. `invoke`/`handle` for request-response; `scriptLoaded`,
 * `positionChanged` and `overlayViewChanged` are main→renderer broadcasts;
 * `overlay:nudge` is a renderer→main send. */
export const IpcChannel = {
  loadScript: "script:load",
  getScript: "script:get-current",
  getLastDocUrl: "settings:get-last-doc-url",
  // Clear forgets the script as well as putting it down (ADR-0039).
  clearScript: "script:clear",
  scriptLoaded: "script:loaded",
  getPosition: "overlay:get-position",
  positionChanged: "overlay:position",
  // Ticket 08 — the prompt's font size.
  //
  // Four geometry channels used to sit here — read the bounds, push new bounds,
  // move without resizing, show or hide — and all four were the renderer moving
  // a window the OS would not move for it. One-window ticket 03 gave the
  // prompter a titlebar and edges, so the OS moves it and the renderer has
  // nothing left to say about where it is. What survives is the font size, which
  // was never geometry.
  getOverlayView: "overlay:get-view",
  overlayViewChanged: "overlay:view",
  // Ticket 09 — scroll mode and status indicators.
  getScrollState: "overlay:get-scroll-state",
  scrollStateChanged: "overlay:scroll-state",
  // Ticket 10 — refresh with diff-anchoring, and the recording flag that greys
  // it (FR-16/FR-17).
  refreshScript: "script:refresh",
  getRecordingStatus: "recording:get-status",
  recordingStatusChanged: "recording:status",
  // Ticket 11 — capture device enumeration and selection (FR-41/FR-42). The
  // renderer is the only context that can enumerate, so `reportDevices` runs
  // renderer→main; everything else mirrors the other state channels.
  reportDevices: "devices:report",
  getDeviceState: "devices:get-state",
  deviceStateChanged: "devices:state",
  selectDevice: "devices:select",
  setCaptureFormat: "devices:set-format",
  // Web-ui ticket 06 — the two toggles on the control bar. A separate channel
  // from `selectDevice` because it is a separate question: that one is which
  // camera, this one is whether there is one at all.
  setDeviceOff: "devices:set-off",
  // Ticket 12 — the recording engine (FR-43 to FR-49). The transport commands
  // are renderer→main sends that the shortcuts land on too; `captureCommand`
  // and `captureEvent` are the two halves of the engine talking to each other
  // across the process boundary that `MediaRecorder` forces.
  startRecording: "recording:start",
  togglePauseRecording: "recording:toggle-pause",
  stopRecording: "recording:stop",
  captureCommand: "recording:capture-command",
  captureEvent: "recording:capture-event",
  // Ticket 13 — the launch-time recovery offer (FR-47). Mirrors the other state
  // channels: the host scans at launch and owns the offers, the renderer pulls
  // them on mount and follows the broadcast.
  getRecoveryState: "recovery:get-state",
  recoveryStateChanged: "recovery:state",
  recoverSession: "recovery:recover",
  dismissSession: "recovery:dismiss",
  // Web ticket 13 — the one action in the offer that deletes. Separate from
  // `dismissSession` because the two answers are different: "not now" keeps the
  // footage and asks again, this one does not. A browser needs it because an
  // origin has a quota rather than a disk, so abandoned sessions are what can
  // stop the next take from starting.
  discardSession: "recovery:discard",
  // Ticket 14 — the microphone fork and live speech-following (FR-31 to FR-38).
  // `speechFrame` is the fork itself crossing the process boundary: the renderer
  // opens the device and taps it, main owns the recogniser. The rest mirrors the
  // other state channels.
  speechFrame: "speech:frame",
  getFollowerState: "speech:get-state",
  followerStateChanged: "speech:state",
  toggleRehearsal: "speech:toggle-rehearsal",
  // Prompter-tweaks ticket 01 — the phone's Play key: run the constant crawl
  // with no take. Every host implements it so all keep the one contract; only
  // the phone draws a key for it.
  toggleCrawl: "overlay:toggle-crawl",
  resyncFollower: "speech:resync",
  // Ticket 15 — voice commands (FR-54 to FR-56). Mirrors the other state
  // channels: the host owns the setting, the collision check and the
  // confirmation window, and every view is a view onto them.
  getVoiceCommandState: "voice:get-state",
  voiceCommandStateChanged: "voice:state",
  setVoiceCommandsEnabled: "voice:set-enabled",
  // Ticket 16 — the settings screens (FR-61 to FR-64). Same shape as everything
  // above: the host owns the settings, every view is a view onto them, and every
  // setter is send-and-forget because none of them can be refused.
  //
  // Four `hotkeys:` channels sat here until one-window ticket 06 — the switches
  // that were all v1's desktop offered instead of rebinding, because a key
  // registered with the operating system is one another application can already
  // hold, and switching it off was the only answer to that. Nothing registers
  // now, so there is nothing to switch off and nothing to fail; the
  // `shortcuts:` channels below are what both hosts use.
  getOverlaySettings: "settings:get-overlay",
  overlaySettingsChanged: "settings:overlay",
  setOverlayDefaults: "settings:set-overlay-defaults",
  setConstantSpeedWpm: "settings:set-wpm",
  saveOverlayAsDefaults: "settings:save-overlay-as-defaults",
  resetOverlaySettings: "settings:reset-overlay",
  getOutputDirectory: "output:get-directory",
  revealOutputDirectory: "output:reveal",
  revealLastTake: "output:reveal-take",
  resetDevices: "devices:reset",
  /**
   * The commands a keypress runs, and the bindings behind them (web ticket 10,
   * one-window ticket 06).
   *
   * These are the one place the contract had a hole rather than a host that had
   * nothing to say. Nudging, cycling the scroll mode, stepping the font, stepping
   * the window's opacity and marking the take were all *global hotkeys* on the
   * desktop: they arrived straight into main from `globalShortcut` and so never
   * crossed a process boundary, which meant a renderer had no way to ask for any
   * of them. A tab has no global hotkeys, so the page had to hear the key and
   * something had to carry it — and now neither host has them, so every one of
   * these is how a shortcut reaches the thing it drives.
   *
   * Sends rather than requests, like the transport commands they sit beside, and
   * for the same reason: the host broadcasts what changed, and that broadcast is
   * what every view draws itself from.
   *
   * `overlay:step-opacity` and `recording:mark` are the desktop's alone, because
   * a tab is not a window over anything and has no file on a disk to write a
   * marker beside. The web's bridge still declares them and the core worker still
   * leaves them unhandled, which is a refusal that names the channel rather than
   * a silence.
   */
  nudgePosition: "overlay:nudge",
  restartPosition: "overlay:restart",
  cycleScrollMode: "overlay:cycle-mode",
  setScrollMode: "overlay:set-mode",
  cycleTextAlign: "overlay:cycle-text-align",
  cycleSideAlign: "overlay:cycle-side-align",
  stepFontSize: "overlay:step-font",
  stepOpacity: "overlay:step-opacity",
  markTake: "recording:mark",
  getShortcutState: "shortcuts:get-state",
  shortcutStateChanged: "shortcuts:state",
  setShortcutBinding: "shortcuts:set-binding",
  resetShortcuts: "shortcuts:reset",
} as const;

/** Broadcast to every window when a script loads, so both renderers stay views
 * onto the single copy of state held in the main process. */
export interface ScriptLoadedPayload {
  docId: string;
  text: string;
  /** The document's own name where the source knows it — what the recording
   * file is named after (FR-48). */
  title?: string;
}

/**
 * What the prompter renderer needs to draw the current view (ticket 08): the
 * font size and the anchor, both applied as CSS.
 *
 * It kept its name and its channel rather than becoming `fontSizeChanged`,
 * because what it *is* has not changed — this is the live layer of FR-63,
 * broadcast so every view of the prompt is drawn from one set of numbers. What
 * went with one-window ticket 03 is `adjustMode`, which described a click-through
 * window being made briefly interactive, and `visible`, which was the show/hide
 * switch's view onto a window that now minimises like any other. That left one
 * field, described here as the one thing in it that was never geometry.
 *
 * **`anchor` is the second such thing** (text-align ticket 01): where down the
 * prompter the reading line sits, as a fraction of its height. Not geometry
 * either — it is a fraction of whatever the prompter's height turns out to be,
 * so it means the same in a maximised tab as in a 480px window, which is exactly
 * what the four channels that went could not say.
 *
 * Required rather than optional, and that is deliberate. `DEFAULT_VIEW` is
 * written out in two components and both hosts build this state of their own, so
 * a required field makes the compiler list every site that has to be taught
 * instead of leaving one silently anchored at a third.
 *
 * **`side` is the third** (text-align ticket 02): where a phone held sideways
 * puts its column across the screen, 0 left to 1 right. Only the phone draws
 * from it; the other views receive it and ignore it.
 *
 * **`mirror` is the fourth** (prompter-display ticket 02): whether the script is
 * drawn flipped left to right, for a screen read through teleprompter glass.
 * The web draws it and has the control. The phone and the desktop read the
 * stored value and offer no control yet.
 *
 * **`lineSpacing` is the fifth** (prompter-display ticket 04): how far apart the
 * lines are drawn, as a factor on each host's own line height, so 1 is the
 * spacing every prompter had before it. The web has the control, and the web
 * and the desktop draw it, because the desktop's pane is the same `Prompter`
 * under the same stylesheet. The phone carries it and draws it from ticket 05.
 */
export interface OverlayViewState {
  fontSize: number;
  anchor: number;
  side: number;
  mirror: boolean;
  lineSpacing: number;
}

/** The always-visible overlay status the renderer draws (FR-28, ticket 09): the
 * current scroll mode, the off-script indicator (raised whenever the follower is
 * holding position — ticket 14), and the constant-speed rate shown alongside the
 * mode label. */
export interface OverlayScrollState {
  mode: ScrollMode;
  offScript: boolean;
  wpm: number;
  /**
   * True when speech-following cannot run at all — no model, or a recogniser that
   * failed to load (ticket 14).
   *
   * The overlay needs this because of goal G5: without it, a prompter with no
   * recogniser sits in `speech` mode showing `SPEECH` and simply never moves, and
   * the only explanation is in the other window. The mode indicator has to tell the
   * truth about whether the mode it names can actually do anything.
   */
  speechUnavailable: boolean;
  /**
   * True while Play is on: the constant crawl was started by hand, with no take
   * (prompter-tweaks ticket 01, ADR-0027).
   *
   * Play's state and not the timer's. A paused take freezes the crawl with Play
   * still on, and a crawl the recorder runs on its own is not Play. The phone's
   * reading-group key reads this to say `Play` or `Pause`.
   */
  playing: boolean;
}

/** The API the preload script exposes on `window.cuekards`. */
export interface CuekardsApi {
  /** Load a script from a Google Doc URL (FR-12/13). */
  loadScript(url: string): Promise<LoadResult>;
  /** The script currently loaded in the main process, or null if none — lets a
   * window that mounts after a load pull the state instead of racing the
   * `scriptLoaded` broadcast. */
  getScript(): Promise<ScriptLoadedPayload | null>;
  /** The last URL loaded in a previous session, for prefilling on launch (FR-18). */
  getLastDocUrl(): Promise<string | undefined>;
  /** Forget the loaded script and the remembered reference to it, so neither a
   * refresh nor the next launch brings it back (ADR-0039). A request rather than
   * a send: on the web it resolves only after the forgetting write has been
   * posted to the page, so storage is already clear when it does. */
  clearScript(): Promise<void>;
  /** Subscribe to script-loaded broadcasts; returns an unsubscribe function. */
  onScriptLoaded(listener: (payload: ScriptLoadedPayload) => void): () => void;
  /** The current anchored word position held in the main process — the index
   * into the spoken-word stream, or -1 before the first word. Lets an overlay
   * that mounts after a nudge pull the position instead of racing the
   * `positionChanged` broadcast. */
  getPosition(): Promise<number>;
  /** Subscribe to position-changed broadcasts (the manual nudge in ticket 07,
   * the follower later); returns an unsubscribe function. */
  onPositionChanged(listener: (position: number) => void): () => void;

  /** The prompt's current font size — pulled on mount so a window that opens
   * after a shortcut fired reflects it (ticket 08). */
  getOverlayView(): Promise<OverlayViewState>;
  /** Subscribe to overlay view changes — which now means the font being stepped;
   * returns an unsubscribe function. */
  onOverlayViewChanged(listener: (state: OverlayViewState) => void): () => void;

  /** The current scroll mode and off-script status — pulled on mount so a window
   * that opens after the mode was cycled reflects it (ticket 09). */
  getScrollState(): Promise<OverlayScrollState>;
  /** Subscribe to scroll-state changes (mode cycled, off-script raised/cleared);
   * returns an unsubscribe function. */
  onScrollStateChanged(
    listener: (state: OverlayScrollState) => void,
  ): () => void;

  /** Re-fetch the current Google Doc and re-anchor the overlay to the sentence
   * the user was on (FR-16). Returns the load result, or null when there is no
   * doc loaded to refresh. A no-op while recording (FR-17). */
  refreshScript(): Promise<LoadResult | null>;
  /** The current recording status — pulled on mount so a window that opens
   * mid-take shows the take rather than an idle transport (FR-49). */
  getRecordingStatus(): Promise<RecordingStatus>;
  /** Subscribe to recording-status changes; returns an unsubscribe function. */
  onRecordingStatusChanged(
    listener: (status: RecordingStatus) => void,
  ): () => void;

  /** Start a take (FR-43). Mirrors the `Ctrl+Alt+R` hotkey; both land on the
   * same controller in main, so the transport and the hotkey can never disagree
   * about what is rolling. */
  startRecording(): void;
  /** Pause a rolling take, or resume a paused one — one entry point because
   * `Ctrl+Alt+P` is one key (FR-43). */
  togglePauseRecording(): void;
  /** End the take and write the MP4 (FR-46). */
  stopRecording(): void;

  /** Subscribe to capture commands from main. Only the window that owns the
   * camera acts on these — see `CaptureEngine`. Returns an unsubscribe
   * function. */
  onCaptureCommand(listener: (command: CaptureCommand) => void): () => void;
  /** Report a capture event — a chunk of media, or the take starting, ending or
   * failing — back to main, which owns the session and the file. */
  sendCaptureEvent(event: CaptureEvent): void;

  /** Push an enumeration of the capture devices to the main process (FR-41).
   * The renderer owns `navigator.mediaDevices`; main owns the state, so the
   * renderer reports on mount and on every `devicechange`. Main normalises what
   * arrives, so the raw `MediaDeviceInfo` fields go over as they are. */
  reportDevices(
    devices: { deviceId: string; kind: string; label: string }[],
  ): void;
  /** The current device state — pulled on mount so a window that opens after an
   * enumeration reflects it. */
  getDeviceState(): Promise<DeviceState>;
  /** Subscribe to device-state changes — hot-plug, a new selection, a device
   * that vanished; returns an unsubscribe function. */
  onDeviceStateChanged(listener: (state: DeviceState) => void): () => void;
  /** Override the device used for one kind, or pass null to return to the
   * system default (FR-42). */
  selectDevice(kind: CaptureKind, deviceId: string | null): void;
  /** Override capture resolution and framerate, or pass null to return to the
   * device's native format (FR-42). */
  setCaptureFormat(format: CaptureFormat | null): void;
  /**
   * Switch one device off, or back on — the control bar's two toggles (web-ui
   * ticket 06).
   *
   * The surface showing the self-view drops the device from its own preview on
   * the press, because that is where the stream is. This is how the *take* finds
   * out: it is opened by whoever owns the recording state, out of
   * `DeviceController.getConstraints`, and a take that named a microphone the
   * room had switched off would record the sound the room promised it would not.
   */
  setDeviceOff(kind: CaptureKind, off: boolean): void;

  /** The takes an interrupted session left on disk, found at launch (FR-47) —
   * pulled on mount, since the scan happens before this window exists. */
  getRecoveryState(): Promise<RecoveryState>;
  /** Subscribe to recovery-state changes — a recovery finishing, an offer
   * declined; returns an unsubscribe function. */
  onRecoveryStateChanged(listener: (state: RecoveryState) => void): () => void;
  /** Turn one interrupted take's chunks into the MP4 it should have been. */
  recoverSession(id: string): void;
  /** Decline an offer, or clear a notice about one already dealt with. Never
   * deletes the chunks — the next launch offers them again. */
  dismissSession(id: string): void;
  /** Delete one interrupted take's footage for good (web ticket 13). The only
   * action in the offer that throws anything away, which is why it is not
   * {@link dismissSession} with a flag: in a browser the storage it frees is
   * what the next take needs to be allowed to start. */
  discardSession(id: string): void;

  /**
   * Hand main one frame of forked microphone audio (FR-31, ticket 14).
   *
   * Called several times a second by the audio tap in the window that owns the
   * microphone, and by nothing else — this is the fork crossing the process
   * boundary, not a general-purpose channel.
   */
  sendSpeechFrame(samples: Float32Array): void;
  /** The follower's current state — pulled on mount so a window that opens
   * mid-rehearsal reflects it. */
  getFollowerState(): Promise<FollowerState>;
  /** Subscribe to follower-state changes; returns an unsubscribe function. */
  onFollowerStateChanged(
    listener: (state: FollowerState) => void,
  ): () => void;
  /** Start or stop following without recording, for rehearsal (FR-37). */
  toggleRehearsal(): void;
  /** Start or pause the constant crawl without a take — the phone's Play key
   * (prompter-tweaks ticket 01). It turns itself off at the last word, on leaving
   * `constant`, and when a take ends. */
  toggleCrawl(): void;
  /** Force a match attempt against a widened window (FR-36). Mirrors the
   * `Ctrl+Alt+Enter` hotkey. */
  resyncFollower(): void;

  /** Whether voice commands are on, whether the loaded script has disabled them
   * (FR-55), and whether a spoken stop is waiting to be confirmed (FR-56) —
   * pulled on mount, like every other state channel. */
  getVoiceCommandState(): Promise<VoiceCommandState>;
  /** Subscribe to voice-command state changes; returns an unsubscribe function. */
  onVoiceCommandStateChanged(
    listener: (state: VoiceCommandState) => void,
  ): () => void;
  /** Turn voice commands on or off, remembered across restarts (FR-54). */
  setVoiceCommandsEnabled(enabled: boolean): void;

  /** The overlay defaults and constant-speed rate the settings screen edits
   * (FR-61) — pulled on mount, like every other state channel. */
  getOverlaySettings(): Promise<OverlaySettingsState>;
  /** Subscribe to overlay-settings changes; returns an unsubscribe function. */
  onOverlaySettingsChanged(
    listener: (state: OverlaySettingsState) => void,
  ): () => void;
  /** Store new overlay defaults *and* put them on the live overlay (ADR-0004) —
   * these are live controls, so a change shows itself rather than waiting to be
   * applied. */
  setOverlayDefaults(defaults: OverlayDefaults): void;
  /** Store the constant-speed rate in words per minute (FR-35/FR-61). */
  setConstantSpeedWpm(wpm: number): void;
  /** Make the overlay as it currently sits the stored default — the "explicitly
   * saved" half of FR-63. */
  saveOverlayAsDefaults(): void;
  /** Restore the built-in overlay defaults and rate, and apply them (FR-64). */
  resetOverlaySettings(): void;

  /** The folder finished recordings are written to (FR-48/FR-62) — fixed in v1,
   * so this is read-only. */
  getOutputDirectory(): Promise<string>;
  /** Open that folder in Explorer (FR-62). */
  revealOutputDirectory(): void;
  /**
   * Open Explorer on the take that just finished, with the file itself selected.
   *
   * Takes no path, deliberately. The renderer knows the filename — it is on
   * screen — but a channel that reveals whatever path it is handed is a renderer
   * asking the main process to open arbitrary places on the disk, and there is
   * nothing this needs that answer for. Main already holds the last output on the
   * recording controller, so it looks it up itself and there is no path to
   * validate because none crossed.
   *
   * Falls back to the output folder when there is no last take, which is what a
   * fresh session has.
   */
  revealLastTake(): void;
  /** Drop every device override, back to the system defaults at the device's
   * native format (FR-64). */
  resetDevices(): void;

  /**
   * Step the anchor one content line forward (+1) or back (-1) — the escape
   * hatch that works in every scroll mode (FR-36, story 32).
   *
   * Mirrors the `Ctrl+Alt+Down` / `Ctrl+Alt+Up` hotkeys on the desktop and lands
   * on the same controller, so the key and the channel can never disagree about
   * where the reader is.
   */
  nudgePosition(direction: NudgeDirection): void;
  /**
   * Put the reader back at the top of the script (story 32's other direction).
   *
   * Not a reload: the doc is not re-fetched and the recogniser is not rebuilt, so
   * this is the cheap "start again" that rehearsing to the middle of a script
   * leaves you needing. Works in every scroll mode and takes the follower's window
   * with it, exactly as a nudge does.
   */
  restartPosition(): void;
  /** Cycle to the next scroll mode — speech, manual, constant (FR-35). Never
   * touches the position or a recording, so it is safe mid-take. */
  cycleScrollMode(): void;

  /**
   * Go to a named scroll mode (flow ticket 06).
   *
   * The same guarantees as {@link cycleScrollMode} — never the position, never a
   * recording — and this is what a control that knows which mode it wants calls,
   * rather than cycling until the right one comes up.
   *
   * **The difference between the two is durability, not effect.** Naming a mode
   * is somebody deciding; cycling is somebody mid-take reaching for the next one.
   * So the web host remembers this one in `Settings.followVoice` and does not
   * remember a cycle. The desktop remembers neither: it has no speech model to
   * download and so nothing to have decided.
   */
  setScrollMode(mode: ScrollMode): void;

  /**
   * Move the reading line to the next of its three stops — top, middle, bottom
   * (text-align ticket 01).
   *
   * Modelled on {@link cycleScrollMode} and answering to the opposite half of the
   * durability argument {@link setScrollMode} makes. Cycling a *mode* is somebody
   * mid-take reaching for the next one and is not remembered; cycling this is
   * somebody answering where their webcam is bolted, which is true of their desk
   * and not of this take — so the host writes it through to the stored defaults.
   * It is a cycle rather than a setter for the reason there is no slider: three
   * stops, pressed while watching the words move, is the whole control.
   *
   * Never refused, a running take included. A reader who realises mid-take that
   * they are looking below their lens should be able to fix it rather than stop.
   */
  cycleTextAlign(): void;

  /**
   * Move a sideways prompter's column to the next of its three places — left,
   * centre, right (text-align ticket 02).
   *
   * {@link cycleTextAlign}'s twin for a phone on its side, where the lens is at
   * one end of the screen, and remembered for the same reason. A channel of its
   * own rather than a payload on that one, which every host sends bare. Only the
   * phone's host answers it; the others have no sideways prompter.
   */
  cycleSideAlign(): void;

  /**
   * Step the prompt's font size one notch up (+1) or down (-1) mid-session
   * (FR-27, story 43).
   *
   * Changes the *live* prompter and not the stored default, which is FR-63 and
   * the reason this is a channel of its own rather than a `setOverlayDefaults`
   * with a different font in it.
   */
  stepFontSize(direction: StepDirection): void;
  /**
   * Step the prompter window's opacity one notch up (+1) or down (-1) (FR-27).
   *
   * The desktop's alone: a browser tab is not a window over anything, so the
   * command has no meaning there and the web's rows do not carry it. The web's
   * bridge still declares it, because the contract is one contract and a host
   * that cannot do something refuses it by name.
   */
  stepOpacity(direction: StepDirection): void;
  /**
   * Write a good or bad marker against the moment the take is at (story 67).
   *
   * Desktop-only for the same reason, one step further along: the markers go into
   * the sidecar beside a file on a disk, and choosing where that file goes is the
   * thing a browser cannot do.
   */
  markTake(quality: TakeMarkerQuality): void;

  /**
   * Which key each in-window shortcut is on (web ticket 10, story 38) — pulled on
   * mount, like every other state channel. The renderer is the only thing that
   * can hear a keyboard, so this is what it listens for.
   */
  getShortcutState(): Promise<ShortcutState>;
  /** Subscribe to shortcut-binding changes; returns an unsubscribe function. */
  onShortcutStateChanged(listener: (state: ShortcutState) => void): () => void;
  /** Move one command onto a different key (story 38). Refused silently for a key
   * the browser keeps for itself or one another command already has — the screen
   * checks both with the same functions and says which, before sending. */
  setShortcutBinding(command: ShortcutCommand, chord: string): void;
  /** Put every shortcut back on its built-in key (FR-64). */
  resetShortcuts(): void;
}

declare global {
  interface Window {
    cuekards: CuekardsApi;
  }
}
